import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { pluralizeWithCount } from "@common/helpers/string";
import { Counterparty } from "@common/types/counterparty";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import {
    RuleConditionType,
    RuleGroupType,
    RuleSource,
    TransactionRule,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import { FiltersTypes } from "@common/types/filters/general";
import { MatchingRuleData } from "@components/transactions/PaginatedTransactionsTable.context";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useRuleEntities } from "@hooks/useRuleEntities";
import { EntityAvatarGroup } from "@components/avatar/EntityAvatarGroup";
import { useOverlays } from "contexts/OverlayContext";
import { Button } from "@design-system/Button/Button";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getApiErrorMessage } from "@api/apiError";
import {
    useAddRuleReview,
    useRevokeRuleReview,
    useRuleRemovalMutation,
    useRuleReviews,
    useUpdateRule,
} from "@api/rules.api";
import { useUser } from "@hooks/useUser";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { toTransactionRuleReviewer } from "@common/schemas/transactionRuleReview.schema";
import { AlertIcon, DragHandle6DotIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { RulesDrawer } from "../RulesDrawer";
import { TransactionRuleConditions } from "../../../transactionRules/TransactionRuleConditions";
import { TransactionRuleActions } from "../../../transactionRules/TransactionRuleActions";
import { EditRuleModal } from "../EditRuleModal";
import { getRuleTitle } from "../utils";
import { RuleReviewedCheck } from "./RuleReviewedCheck";
import styles from "../RulesTable.module.scss";

/**
 * RuleRow displays a single rule row, including drag handle and visual indicators.
 */
interface RuleRowProps {
    readonly rule: TransactionRule;
    readonly isOver: boolean;
    readonly listeners?: React.HTMLAttributes<HTMLElement>;
    readonly attributes: React.HTMLAttributes<HTMLElement>;
    readonly isGroupDragged: boolean;
    readonly counterparties: Counterparty[];
    readonly normalizedDescriptions: NormalizedDescriptionInfo[];
    readonly transactionCounts: Record<string, number>;
    readonly matchingCounts: Record<string, number>;
    readonly isUnreachable: boolean;
    readonly isDuplicate: boolean;
    readonly hasDisabledAccount?: boolean;
    readonly groupId: string;
    readonly groupType: RuleGroupType;
    readonly routeRuleId: string | null;
    readonly onRouteRuleClose: () => void;
}

const RuleAlert = ({
    isUnreachable,
    isDuplicate,
    rule,
    hasDisabledAccount,
}: {
    isUnreachable: boolean;
    isDuplicate: boolean;
    rule: TransactionRule;
    hasDisabledAccount?: boolean;
}) => {
    if (!isUnreachable && !isDuplicate && !hasDisabledAccount) {
        return null;
    }

    if (rule.isDisabled) {
        return null;
    }

    const message = [];
    if (isUnreachable) {
        message.push(
            "This rule conflicts with another rule in the group. Move it above the rule that covers it.",
        );
    }
    if (isDuplicate) {
        message.push("This rule is a duplicate of another rule.");
    }
    if (hasDisabledAccount) {
        message.push("This rule is targeting an archived account.");
    }

    return (
        <Tooltip content={message.join("\n")}>
            <div>
                <AlertIcon className="icon-color-yellow-500 mr-2" />
            </div>
        </Tooltip>
    );
};
const getDisableModalTitle = (rule: TransactionRule): string => {
    if (rule.source === RuleSource.NORMALIZATION) {
        return rule.isDisabled ? "Enable Kick rule?" : "Archive Kick rule?";
    }
    return rule.isDisabled
        ? "Are you sure you want to enable this rule?"
        : "Are you sure you want to archive this rule?";
};

const getDisableModalQuestion = (rule: TransactionRule): string => {
    if (rule.source === RuleSource.NORMALIZATION) {
        return rule.isDisabled
            ? "Kick uses this rule on new transactions again. Past transactions don't change."
            : "Kick stops using this rule on new transactions. Past transactions don't change. You can enable it again anytime.";
    }
    return rule.isDisabled
        ? "After enabling, the rule will be applied to future transactions. You can archive it later."
        : "After archiving, the rule will not be applied to future transactions. You can re-enable it later.";
};

export const RuleRow: React.FC<RuleRowProps> = ({
    rule,
    isOver,
    listeners = {},
    attributes = {},
    isGroupDragged,
    counterparties,
    normalizedDescriptions,
    transactionCounts,
    matchingCounts,
    isUnreachable,
    isDuplicate,
    hasDisabledAccount,
    groupId,
    groupType,
    routeRuleId,
    onRouteRuleClose,
}) => {
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();
    const { openOverlay } = useOverlays();
    const { toast, error } = useToaster();
    const [showEdit, setShowEdit] = useState(false);
    const updateRule = useUpdateRule();
    const [showDisable, setShowDisable] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const removal = useRuleRemovalMutation(rule);
    const user = useUser();
    const { archiveAiRules } = useTypedFlags();
    const { data: reviews = [] } = useRuleReviews(rule.id);
    const addReview = useAddRuleReview();
    const revokeReview = useRevokeRuleReview();
    const isReviewedByCurrentUser = reviews.some(
        (review) => review.userId === user.id,
    );
    const toggleReview = useCallback(() => {
        const params = {
            workspaceId: rule.workspaceId,
            ruleId: rule.id,
            userId: user.id,
        };
        if (isReviewedByCurrentUser) {
            revokeReview.mutate(params);
        } else {
            addReview.mutate({
                ...params,
                reviewer: toTransactionRuleReviewer(user),
            });
        }
    }, [rule, user, isReviewedByCurrentUser, addReview, revokeReview]);
    const isTransferRule = rule.conditions.some(
        (condition) => condition.transferDirection,
    );
    const isAutoCreatedSimilarityRule =
        rule.source === RuleSource.NORMALIZATION;
    const canToggleArchive =
        !isAutoCreatedSimilarityRule ||
        archiveAiRules ||
        Boolean(rule.isDisabled);
    const isRouteRuleOpen = routeRuleId === rule.id;
    const isEditDrawerOpen = showEdit || isRouteRuleOpen;

    const ruleEntities = useRuleEntities(rule);
    const contentCellClassName = classNames(styles.td, {
        [styles.archivedContent]: rule.isDisabled,
    });

    const closeEditDrawer = useCallback(() => {
        setShowEdit(false);
        if (isRouteRuleOpen) {
            onRouteRuleClose();
        }
    }, [isRouteRuleOpen, onRouteRuleClose]);

    const overrideRule = useCallback(() => {
        openOverlay("createRule", {
            definition: {
                conditions: rule.conditions,
                actions: rule.actions,
            },
            source: RuleSource.SETTINGS,
            groupType: RuleGroupType.TRANSACTION,
        });
    }, [openOverlay, rule]);

    const duplicateRule = useCallback(() => {
        openOverlay("createRule", {
            definition: {
                groupId,
                conditions: rule.conditions,
                actions: rule.actions,
            },
            source: RuleSource.SETTINGS,
            groupType,
        });
    }, [openOverlay, rule, groupId, groupType]);

    const openRuleTransactions = useCallback(() => {
        const entityCondition = rule.conditions.find(
            (condition) => condition.conditionType === RuleConditionType.ENTITY,
        );

        const filters: FiltersTypes = {
            transactionRulesIds: [rule.id],
            entityIds: entityCondition?.value as number[],
        };

        const matchingRuleData: MatchingRuleData = {
            ruleId: rule.id,
            conditions: rule.conditions,
            groupType: isTransferRule
                ? RuleGroupType.TRANSFER
                : RuleGroupType.TRANSACTION,
            // AI cards show the live matching count, so open the drawer already in
            // matching mode to keep its default total consistent with the card.
            defaultToMatchingMode: isAutoCreatedSimilarityRule,
        };

        openGlobalTransactionViewDrawer(filters, { matchingRuleData });
    }, [
        openGlobalTransactionViewDrawer,
        rule,
        isTransferRule,
        isAutoCreatedSimilarityRule,
    ]);

    return (
        <>
            <td
                className={contentCellClassName}
                style={{ position: "relative" }}
            >
                <div>
                    <TransactionRuleConditions
                        rule={rule}
                        counterparties={counterparties}
                        normalizedDescriptions={normalizedDescriptions}
                        transferDirection={
                            isTransferRule
                                ? TransferRuleDirection.FROM
                                : undefined
                        }
                        hideEntityCondition={!isTransferRule}
                    />
                </div>
                {isOver && !isGroupDragged && (
                    <>
                        <div
                            className={classNames(
                                styles.insertionIndicator,
                                styles.top,
                                styles.withDot,
                            )}
                        />
                        <div
                            className={classNames(
                                styles.insertionIndicatorDot,
                                styles.top,
                            )}
                        />
                    </>
                )}
            </td>
            {isTransferRule && (
                <td
                    className={contentCellClassName}
                    style={{ position: "relative" }}
                >
                    <div>
                        <TransactionRuleConditions
                            rule={rule}
                            counterparties={counterparties}
                            normalizedDescriptions={normalizedDescriptions}
                            transferDirection={TransferRuleDirection.TO}
                        />
                    </div>
                </td>
            )}
            <td
                className={contentCellClassName}
                style={{ position: "relative" }}
            >
                <TransactionRuleActions
                    rule={rule}
                    counterparties={counterparties}
                />
                {isOver && !isGroupDragged && (
                    <div
                        className={classNames(
                            styles.insertionIndicator,
                            styles.top,
                        )}
                    />
                )}
            </td>
            {!isTransferRule && (
                <td
                    className={contentCellClassName}
                    style={{ position: "relative" }}
                >
                    {ruleEntities.length > 0 && (
                        <EntityAvatarGroup
                            entities={ruleEntities}
                            max={3}
                            size="xs"
                            className="pt-1"
                        />
                    )}
                </td>
            )}
            <td className={styles.td} style={{ position: "relative" }}>
                <HStack gap={12} align="center" justify="between">
                    <HStack gap={12} align="center">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={openRuleTransactions}
                            className={styles.numberOfTransactions}
                        >
                            {isAutoCreatedSimilarityRule
                                ? (matchingCounts[rule.id] ?? 0)
                                : (transactionCounts[rule.id] ?? 0)}
                        </Button>
                        {rule.isDisabled && (
                            <Tag size="sm" color="neutral">
                                Archived
                            </Tag>
                        )}
                        <RuleReviewedCheck reviews={reviews} />
                        <RuleAlert
                            isUnreachable={isUnreachable}
                            isDuplicate={isDuplicate}
                            hasDisabledAccount={hasDisabledAccount}
                            rule={rule}
                        />
                    </HStack>
                    <HStack gap={12}>
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            tabIndex={-1}
                            aria-label="Drag rule"
                            className={styles.dragButton}
                            {...listeners}
                            {...attributes}
                        >
                            <DragHandle6DotIcon />
                        </Button>
                        <Menu.More className={styles.managementDropdown}>
                            {isAutoCreatedSimilarityRule ? (
                                <Menu.Item onClick={overrideRule}>
                                    Override
                                </Menu.Item>
                            ) : (
                                <Menu.Item onClick={() => setShowEdit(true)}>
                                    Edit
                                </Menu.Item>
                            )}
                            <Menu.Item onClick={toggleReview}>
                                {isReviewedByCurrentUser
                                    ? "Mark as unreviewed"
                                    : "Mark as reviewed"}
                            </Menu.Item>
                            {!isAutoCreatedSimilarityRule && (
                                <Menu.Item onClick={duplicateRule}>
                                    Duplicate
                                </Menu.Item>
                            )}
                            {canToggleArchive && (
                                <Menu.Item onClick={() => setShowDisable(true)}>
                                    {rule.isDisabled ? "Enable" : "Archive"}
                                </Menu.Item>
                            )}
                            {!isAutoCreatedSimilarityRule && (
                                <Menu.Item
                                    onClick={() => setShowDelete(true)}
                                    destructive
                                >
                                    Delete
                                </Menu.Item>
                            )}
                        </Menu.More>
                    </HStack>
                </HStack>
                {isOver && !isGroupDragged && (
                    <div
                        className={classNames(
                            styles.insertionIndicator,
                            styles.top,
                        )}
                    />
                )}

                {!isAutoCreatedSimilarityRule && (
                    <RulesDrawer
                        open={isEditDrawerOpen}
                        onClose={closeEditDrawer}
                        headerText={getRuleTitle(
                            isTransferRule
                                ? RuleGroupType.TRANSFER
                                : RuleGroupType.TRANSACTION,
                        )}
                    >
                        <EditRuleModal
                            rule={rule}
                            close={closeEditDrawer}
                            normalizedDescriptions={normalizedDescriptions}
                            groupType={
                                isTransferRule
                                    ? RuleGroupType.TRANSFER
                                    : RuleGroupType.TRANSACTION
                            }
                        />
                    </RulesDrawer>
                )}

                <ConfirmationModal
                    show={showDelete}
                    onHide={() => setShowDelete(false)}
                    onConfirm={async () => {
                        await removal.mutateAsync();
                        setShowDelete(false);
                    }}
                    title="Are you sure you want to delete this rule?"
                    question={`This rule affected ${pluralizeWithCount("transaction", transactionCounts[rule.id] ?? 0)}. Deleting it stops it from applying to future transactions. Archive it to turn it off temporarily.`}
                    yes="Delete"
                    yesBtnVariant="destructive"
                />

                <ConfirmationModal
                    show={showDisable}
                    onHide={() => setShowDisable(false)}
                    onConfirm={async () => {
                        const willDisable = !rule.isDisabled;
                        await updateRule
                            .mutateAsync(
                                {
                                    workspaceId: rule.workspaceId,
                                    ruleId: rule.id,
                                    rule: { isDisabled: willDisable },
                                    applyToExistingTransactions: false,
                                    suppressErrorToast: true,
                                },
                                {
                                    onSuccess: () => {
                                        toast(
                                            willDisable
                                                ? "Archived rule"
                                                : "Enabled rule",
                                        );
                                        setShowDisable(false);
                                    },
                                    onError: (updateError) => {
                                        error(
                                            getApiErrorMessage(
                                                updateError,
                                                willDisable
                                                    ? "Failed to archive rule"
                                                    : "Failed to enable rule",
                                            ),
                                        );
                                    },
                                },
                            )
                            .catch(() => undefined);
                    }}
                    title={getDisableModalTitle(rule)}
                    question={getDisableModalQuestion(rule)}
                    yes={rule.isDisabled ? "Enable" : "Archive"}
                    yesBtnVariant={rule.isDisabled ? "primary" : "destructive"}
                />
            </td>
        </>
    );
};
